import React, { ComponentProps, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { OriginalAsset } from '@bynder/image-gen-frontend-lib';
import { notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { getUser } from 'packages/store/user/user.selectors';
import ImageGen from './ImageGenModal';
import AssetsService from '~/services/AssetsService';

const useImageGenModal = (props: {
    handleAsset?: (asset: any) => void;
    shouldCloseAfterSave?: boolean;
    saveButtonLabel?: string;
}) => {
    const sessionId = useRef('');
    const { translate } = useTranslate();

    const permissions = useSelector(getUser)?.permissions || [];

    const [isOpen, setOpen] = useState(false);
    const [originalAsset, setOriginalAsset] = useState<OriginalAsset>(null);
    const [collectionId, setCollectionId] = useState(null);

    type ImageGenInput = { src: string; mime?: never } | { mime: string; src?: never };

    const isAllowedExtension = (extension: string) => {
        const allowedExtensions = ['jpg', 'jpeg', 'bmp', 'gif', 'wbmp', 'png', 'tif', 'tiff', 'webp'];

        return allowedExtensions.includes(extension);
    };

    const isAllowedToUse = (input: ImageGenInput) => {
        // We deprecated these permissions for any in studio features
        // But this the only way to get bynder's level permissions for now
        if (!permissions.includes('assets:ai-edit')) {
            return false;
        }

        const allowedMimeTypes = [
            'image/jpeg',
            'image/bmp',
            'image/gif',
            'image/vnd.wap.wbmp',
            'image/png',
            'image/tiff',
            'image/webp',
        ];

        if ('mime' in input && input.mime) {
            return allowedMimeTypes.includes(input.mime.toLowerCase());
        }

        if ('src' in input && input.src) {
            const extensionMatch = input.src.match(/\.([a-zA-Z0-9]+)(\?.*)?$/);
            const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';

            return isAllowedExtension(extension);
        }

        return false;
    };

    const onOpen = (prop: Pick<ComponentProps<typeof ImageGen>, 'originalAsset' | 'collectionId'>) => {
        sessionId.current = uuidv4();
        setOriginalAsset(prop.originalAsset);
        setCollectionId(prop.collectionId);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onSave = async (data: { fileId: string }) => {
        try {
            const { status, asset } = await AssetsService.saveAIEdit({
                fileId: data.fileId,
                name: `${originalAsset.name}-AI-edited`,
            });

            if (status === 200) {
                if (props.handleAsset) {
                    props.handleAsset(asset);
                }
            } else {
                notify({
                    title: translate('image-gen.modal.save.notification.failed'),
                    variant: 'error',
                });
            }
        } catch (err) {
            notify({
                title: translate('image-gen.modal.save.notification.error'),
                variant: 'error',
            });
            console.error('Error saving AI edited asset:', err);
        } finally {
            if (props.shouldCloseAfterSave) {
                onClose();
            }
        }
    };

    const renderImageGenModal = () => {
        if (!originalAsset) {
            return null;
        }

        return (
            <ImageGen
                originalAsset={originalAsset}
                collectionId={collectionId}
                saveButtonLabel={props.saveButtonLabel}
                sessionId={sessionId.current}
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
            />
        );
    };

    return {
        setImageGenModal: onOpen,
        renderImageGenModal,
        isAllowedToUse,
        isAllowedExtension,
    };
};

export default useImageGenModal;
